import { defineTour } from '../../types';
import { PERMISSIONS, canAny } from './access';

/**
 * T06 — Track and maintain an order (brief §7).
 *
 * For a seat that reads orders (`view_branch` or `view_any`, as the Purchase
 * orders entry is). The list is `/purchase-orders` (GET only); the record
 * steps run on an order the person opened. Receipts are shown to a seat that
 * may see them; the order's changes only to a seat that may make one — the
 * same gates as the record's action card. Document links write an audit entry
 * when opened, so the guide never opens one.
 *
 * Copy: `guidance.tours.T06.*` — draft, for native-language review.
 */
const RECORD = {
  routeId: 'tenant.purchase-order',
  explainKey: 'guidance.tours.T06.openOrder',
} as const;

const mayChangeOrders = (ctx: Parameters<typeof canAny>[0]) =>
  canAny(
    ctx,
    PERMISSIONS.orderIssue,
    PERMISSIONS.orderSend,
    PERMISSIONS.orderAmend,
    PERMISSIONS.orderCancel,
  );

export const T06 = defineTour({
  id: 'T06',
  version: 1,
  portal: 'tenant',
  titleKey: 'guidance.tours.T06.title',
  summaryKey: 'guidance.tours.T06.summary',
  eligible: (ctx) =>
    ctx.portal === 'tenant' && canAny(ctx, PERMISSIONS.orderViewBranch, PERMISSIONS.orderViewAny),
  pageRoutes: ['tenant.purchase-orders', 'tenant.purchase-order'],
  steps: [
    {
      id: 't06-list',
      anchor: 'tenant.orders.list',
      route: 'tenant.purchase-orders',
      titleKey: 'guidance.tours.T06.list.title',
      bodyKey: 'guidance.tours.T06.list.body',
    },
    {
      id: 't06-header',
      anchor: 'tenant.po.header',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T06.header.title',
      bodyKey: 'guidance.tours.T06.header.body',
    },
    {
      id: 't06-status',
      anchor: 'tenant.po.status-dimensions',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T06.status.title',
      bodyKey: 'guidance.tours.T06.status.body',
    },
    {
      id: 't06-responses',
      anchor: 'tenant.po.supplier-responses',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T06.responses.title',
      bodyKey: 'guidance.tours.T06.responses.body',
    },
    {
      // A draft order has no receipts yet: its lines show received and remaining.
      id: 't06-receipts',
      anchor: ['tenant.po.receipts', 'tenant.po.lines'],
      needsRecord: RECORD,
      when: (ctx) => ctx.can(PERMISSIONS.receiptView),
      titleKey: 'guidance.tours.T06.receipts.title',
      bodyKey: 'guidance.tours.T06.receipts.body',
    },
    {
      id: 't06-versions',
      anchor: 'tenant.po.versions',
      needsRecord: RECORD,
      titleKey: 'guidance.tours.T06.versions.title',
      bodyKey: 'guidance.tours.T06.versions.body',
    },
    {
      // From 640px the record's action card; below it the phone's action bar.
      // Neither is shown when no change this seat may make applies (a completed
      // or cancelled order, say); then the step says so (TOUR-04).
      id: 't06-actions',
      anchor: ['tenant.po.actions', 'tenant.po.actions-phone'],
      needsRecord: RECORD,
      absent: { present: 'tenant.po.header', explainKey: 'guidance.tours.T06.actionsAbsent' },
      when: mayChangeOrders,
      titleKey: 'guidance.tours.T06.actions.title',
      bodyKey: 'guidance.tours.T06.actions.body',
      commitNote: true,
    },
  ],
});
